// `scriptctl view --watch` — a live workspace monitor for TEXT or VIDEO ingest.
// Starts a tiny stdlib HTTP server (no deps) that a self-refreshing HTML dashboard
// polls: ingest progress (deriveIngestStatus), the artifact tree (text serial
// context/window md + provisional/final script, OR video per-episode transcript +
// context-fed-to-model + parsed json + registry + timing.jsonl), and any single
// file's content. The dashboard auto-refreshes so a running `scriptctl ingest` is
// watched live and each unit's products stream in as they finish. Runs until Ctrl-C.
// Text and video workspaces use disjoint layouts, so the artifact groups union
// cleanly — a workspace only populates the groups its pipeline produces.

import * as http from "node:http";
import * as fs from "node:fs";
import * as path from "node:path";
import { spawn } from "node:child_process";

import { EXIT_OK, type CommandOutput } from "../../common.js";
import { deriveIngestStatus } from "./status.js";
import type { UsageReport } from "./usage-report.js";

type Dict = Record<string, unknown>;
const strOf = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

interface FileMeta { rel: string; size: number; mtime: number }

// Curated artifact list for the sidebar, grouped and ordered for reviewing an
// ingest run. Only files that exist are returned.
export function listArtifacts(root: string): Record<string, FileMeta[]> {
  const meta = (rel: string): FileMeta | null => {
    try { const s = fs.statSync(path.join(root, rel)); return { rel, size: s.size, mtime: s.mtimeMs }; } catch { return null; }
  };
  const dirFiles = (dir: string, filter: (n: string) => boolean): FileMeta[] => {
    let names: string[] = [];
    try { names = fs.readdirSync(path.join(root, dir)); } catch { return []; }
    return names.filter(filter).sort().map((n) => meta(`${dir}/${n}`)).filter((m): m is FileMeta => !!m);
  };
  const only = (arr: (FileMeta | null)[]): FileMeta[] => arr.filter((m): m is FileMeta => !!m);
  const epFile = (suffix: string): FileMeta[] =>
    dirFiles("ep", (n) => n.endsWith(suffix) && !n.endsWith(".error.json"));
  return {
    结果: only([meta("script.json"), meta("validation.json"), meta("asset-audit.json"), meta("usage.json"), meta("review.html"), meta("timings.json")]),
    // Text serial artifacts (empty for a video workspace).
    上下文: dirFiles("serial", (n) => n.startsWith("context_") && n.endsWith(".md")),
    每窗更新: dirFiles("serial", (n) => n.startsWith("win_") && n.endsWith(".json")),
    篇章梗概: dirFiles("serial", (n) => n.startsWith("arc_") && n.endsWith(".md")),
    前段: dirFiles("pass2", (n) => /^chunk_\d+\.json$/.test(n)),
    // Video serial artifacts (empty for a text workspace). Grouped by kind so an
    // episode's whole story is one scan down the list.
    转录: epFile(".md").filter((m) => !m.rel.endsWith(".context.md")),
    送模上下文: epFile(".context.md"),
    解析: epFile(".json"),
    花名册: only([meta("context.json")]),
    计时: only([meta("timing.jsonl")]),
    中间产物: only([meta("script.initial.json"), meta("provisional_assets.json"), meta("notes.json"), meta("id_map.json"), meta("manifest.json")]),
  };
}

// Parse timing.jsonl (one episode per line) into a compact summary the dashboard
// shows without shipping the whole file. Tolerant: skips malformed lines. Only the
// video pipeline writes timing.jsonl; a text workspace yields an empty summary.
export function readTiming(root: string): {
  episodes: Array<{ episode: number; transcodeMs?: number; uploadMs?: number; generateMs?: number; uploadMB?: string; transcoded?: boolean }>;
  totalMs: number;
} {
  let text = "";
  try { text = fs.readFileSync(path.join(root, "timing.jsonl"), "utf-8"); } catch { return { episodes: [], totalMs: 0 }; }
  const episodes: Array<{ episode: number; transcodeMs?: number; uploadMs?: number; generateMs?: number; uploadMB?: string; transcoded?: boolean }> = [];
  let totalMs = 0;
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    try {
      const j = JSON.parse(t) as { episode?: number; transcodeMs?: number; uploadMs?: number; generateMs?: number; uploadMB?: string; transcoded?: boolean };
      if (typeof j.episode !== "number") continue;
      const row: (typeof episodes)[number] = { episode: j.episode };
      if (typeof j.transcodeMs === "number") { row.transcodeMs = j.transcodeMs; totalMs += j.transcodeMs; }
      if (typeof j.uploadMs === "number") { row.uploadMs = j.uploadMs; totalMs += j.uploadMs; }
      if (typeof j.generateMs === "number") { row.generateMs = j.generateMs; totalMs += j.generateMs; }
      if (typeof j.uploadMB === "string") row.uploadMB = j.uploadMB;
      if (typeof j.transcoded === "boolean") row.transcoded = j.transcoded;
      episodes.push(row);
    } catch { /* skip malformed line */ }
  }
  return { episodes, totalMs };
}

// Token accounting written by the pipelines → usage.json. Returns null when absent
// (run hasn't reached the point where spend is persisted yet).
export function readUsage(root: string): UsageReport | null {
  try {
    const j = JSON.parse(fs.readFileSync(path.join(root, "usage.json"), "utf-8")) as UsageReport;
    return j;
  } catch { return null; }
}

// Read a workspace file, guarding against path traversal outside the root.
export function readSafe(root: string, rel: string): { ok: true; text: string } | { ok: false } {
  const abs = path.resolve(root, rel);
  if (abs !== root && !abs.startsWith(root + path.sep)) return { ok: false };
  try { return { ok: true, text: fs.readFileSync(abs, "utf-8") }; } catch { return { ok: false }; }
}

function send(res: http.ServerResponse, code: number, type: string, body: string): void {
  res.writeHead(code, { "Content-Type": type, "Cache-Control": "no-store" });
  res.end(body);
}

export async function runViewServer(opts: Dict): Promise<[CommandOutput, number]> {
  const root = path.resolve(strOf(opts["workspace_path"] || "workspace"));
  const port = Number(opts["port"]) || 0; // 0 → OS-assigned ephemeral (no conflicts)

  const server = http.createServer((req, res) => {
    const u = new URL(req.url ?? "/", "http://localhost");
    if (u.pathname === "/") return send(res, 200, "text/html; charset=utf-8", DASHBOARD_HTML);
    if (u.pathname === "/api/state") {
      const status = deriveIngestStatus(root);
      return send(res, 200, "application/json", JSON.stringify({ root, status, groups: listArtifacts(root), timing: readTiming(root), usage: readUsage(root) }));
    }
    if (u.pathname === "/api/file") {
      const rel = u.searchParams.get("path") ?? "";
      const r = readSafe(root, rel);
      return r.ok ? send(res, 200, "text/plain; charset=utf-8", r.text) : send(res, 404, "text/plain", "not found");
    }
    send(res, 404, "text/plain", "not found");
  });

  const actualPort = await new Promise<number>((resolve, reject) => {
    server.once("error", reject);
    server.listen(port, "127.0.0.1", () => resolve((server.address() as { port: number }).port));
  });
  const url = `http://127.0.0.1:${actualPort}/`;
  process.stderr.write(`Ingest monitor: ${url}\nWorkspace: ${root}\nPress Ctrl-C to stop.\n`);
  if (process.platform === "darwin" && process.stderr.isTTY) {
    try { spawn("open", [url], { detached: true, stdio: "ignore" }).unref(); } catch { /* best-effort */ }
  }

  // The listening server keeps the process alive after the startup result is
  // emitted. Closing it on either signal lets the CLI exit cleanly.
  process.once("SIGINT", () => server.close());
  process.once("SIGTERM", () => server.close());
  return [{ text: url, data: { url, workspace: root } }, EXIT_OK];
}

const DASHBOARD_HTML = `<!doctype html><html lang="zh"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>scriptctl ingest 监听</title>
<style>
:root{--bg:#f6f7f9;--panel:#ffffff;--panel2:#f0f2f5;--line:#e3e6ea;--fg:#1f2430;--dim:#6b7280;--acc:#2563eb;--ok:#16a34a;--warn:#d97706;--err:#dc2626}
*{box-sizing:border-box}body{margin:0;font:13px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;background:var(--bg);color:var(--fg);height:100vh;display:grid;grid-template-rows:auto auto 1fr}
header{padding:12px 16px;background:var(--panel);border-bottom:1px solid var(--line);display:flex;gap:16px;align-items:center;flex-wrap:wrap}
header .title{font-weight:700;font-size:15px}
.now{font-size:14px}.now b{color:var(--acc)}
.state{padding:3px 12px;border-radius:20px;font-weight:600;font-size:12px}
.state.ready{background:#e7f6ee;color:var(--ok)}.state.incomplete{background:#e8f0fe;color:var(--acc)}.state.failed{background:#fde8e8;color:var(--err)}.state.empty{background:var(--panel2);color:var(--dim)}
.dim{color:var(--dim)}.mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
/* overall bar + stage stepper */
.progress{background:var(--panel);border-bottom:1px solid var(--line);padding:12px 16px}
.ovwrap{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.ovtrack{flex:1;height:10px;background:var(--panel2);border-radius:6px;overflow:hidden}
.ovtrack i{display:block;height:100%;background:linear-gradient(90deg,#3b82f6,#2563eb);border-radius:6px;transition:width .4s}
.ovpct{font-weight:700;min-width:44px;text-align:right}
.stages{display:flex;align-items:stretch;gap:0}
.stage{flex:1;padding:8px 12px;border:1px solid var(--line);background:var(--panel);border-radius:8px;margin-right:8px}
.stage:last-child{margin-right:0}
.stage-h{display:flex;align-items:center;gap:7px;font-weight:600}
.stage .dot{width:9px;height:9px;border-radius:50%;background:#cbd2dc;flex:none}
.stage .cnt{margin-left:auto;font-size:12px;color:var(--dim)}
.stage .track{height:6px;background:var(--panel2);border-radius:4px;margin-top:8px;overflow:hidden}
.stage .track i{display:block;height:100%;width:0;border-radius:4px;transition:width .4s}
.stage.wait{opacity:.7}
.stage.done{border-color:#bfe6cd}.stage.done .dot{background:var(--ok)}.stage.done .track i{background:var(--ok)}.stage.done .stage-h b{color:var(--ok)}
.stage.run{border-color:#bcd3fb;box-shadow:0 0 0 2px #e8f0fe}.stage.run .dot{background:var(--acc);animation:pulse 1.2s infinite}.stage.run .track i{background:var(--acc)}
.stage.fail{border-color:#f3bcbc}.stage.fail .dot{background:var(--err)}.stage.fail .track i{background:var(--err)}
@keyframes pulse{50%{opacity:.35}}
main{display:grid;grid-template-columns:280px 1fr;min-height:0}
aside{background:var(--panel);border-right:1px solid var(--line);overflow:auto;padding:8px}
.grp{margin:8px 4px 2px;color:var(--dim);font-size:11px;font-weight:700;letter-spacing:.04em}
.f{padding:4px 8px;border-radius:6px;cursor:pointer;display:flex;justify-content:space-between;gap:8px;white-space:nowrap;overflow:hidden}
.f:hover{background:var(--panel2)}.f.sel{background:#e8f0fe;color:var(--acc);font-weight:600}
.f .sz{color:var(--dim);font-size:11px}
section{overflow:auto;background:var(--panel)}
.toolbar{position:sticky;top:0;background:var(--panel2);border-bottom:1px solid var(--line);padding:6px 12px;display:flex;gap:14px;align-items:center;font-size:12px}
label{cursor:pointer;user-select:none}.empty{color:var(--dim);padding:34px;text-align:center}
#view{padding:14px 18px;white-space:pre-wrap;word-break:break-word;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px}
#view h1,#view h2{margin:14px 0 4px}#view h1{color:var(--acc);font-size:15px}#view h2{color:var(--warn);font-size:13px}
#view .b{color:var(--dim)}
/* per-episode timing table (video only) */
.timing{padding:8px 16px 4px;background:var(--panel);border-bottom:1px solid var(--line);overflow:auto;max-height:132px}
table.tm{border-collapse:collapse;font-size:12px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
table.tm th,table.tm td{padding:2px 10px 2px 0;text-align:right}table.tm th:first-child,table.tm td:first-child{text-align:left}
table.tm th{color:var(--dim);font-weight:600;border-bottom:1px solid var(--line)}
table.tm td.slow{color:var(--warn)}
</style></head><body>
<header>
  <span class="title">scriptctl ingest</span>
  <span id="stateBadge" class="state empty">—</span>
  <span class="now">当前：<b id="curState">—</b></span>
  <span class="now">耗时 <b id="elapsed">—</b></span>
  <span class="dim" id="eta"></span>
  <span class="now" id="tokWrap" style="display:none">Token <b id="tokTotal">—</b> <span class="dim" id="tokIO"></span></span>
  <span class="dim mono" id="root" style="margin-left:auto"></span>
  <span class="dim" id="tick"></span>
</header>
<div class="progress">
  <div class="ovwrap"><span class="dim">总进度</span><div class="ovtrack"><i id="ovBar"></i></div><span class="ovpct" id="ovPct">0%</span></div>
  <div class="stages" id="stages"></div>
</div>
<div class="timing" id="timingWrap" style="display:none"><table class="tm" id="tmTable"></table></div>
<div class="timing" id="usageWrap" style="display:none"><table class="tm" id="usageTable"></table></div>
<main>
  <aside id="side"></aside>
  <section>
    <div class="toolbar">
      <span id="cur" class="mono dim">未选择文件</span>
      <label style="margin-left:auto"><input type="checkbox" id="follow" checked> 跟随最新集</label>
    </div>
    <div id="view"><div class="empty">左侧选择文件，或等待 ingest 产出…</div></div>
  </section>
</main>
<script>
var NAMES={normalize:'规范化',assemble:'装配',serial:'串行解析',govern:'治理成稿',finalize:'治理成稿',transcribe:'转写',roster:'角色合并',correct:'校正',speaker:'发声源'};
var cur=null, follow=true, lastLatest=null;
var timing=null, timingAt=0; // last status.timings + client time it arrived (avoids clock skew)
function $(s){return document.querySelector(s)}
function fmtDur(ms){if(ms==null)return '—';var s=Math.round(ms/1000);return s>=60?(Math.floor(s/60)+'m'+('0'+(s%60)).slice(-2)+'s'):s+'s'}
// per-episode timing table (video only; hidden when there is no timing.jsonl)
function fmtTok(n){if(n==null)return '—';if(n>=1e6)return (n/1e6).toFixed(2)+'M';if(n>=1e3)return (n/1e3).toFixed(1)+'k';return ''+n}
var TASKNAMES={'ingest.body-start':'正文定位','ingest.text-normalize':'规范化','ingest.serial-govern':'串行治理','ingest.name-fix':'命名修复','ingest.episode-assets':'分集资产','ingest.asset-curate':'资产甄选','ingest.asset-govern':'资产总览','ingest.arc-summary':'篇章梗概','ingest.state-reconcile':'状态归并','ingest.asset-audit':'资产审计','ingest.mention-resolve':'指称消解','ingest.video-serial':'视频串行'};
// Token accounting table + header badge. Hidden until usage.json exists.
function renderUsage(u){
  if(!u||!u.totals){$('#usageWrap').style.display='none';$('#tokWrap').style.display='none';return}
  var t=u.totals;
  $('#tokWrap').style.display='';
  $('#tokTotal').textContent=fmtTok(t.totalTokens);
  var cacheHit=(t.cachedInputTokens||0),cacheNew=(t.cacheCreationTokens||0);
  var cacheStr=(cacheHit||cacheNew)?' · 缓存命中 '+fmtTok(cacheHit)+(cacheNew?'/建 '+fmtTok(cacheNew):''):'';
  $('#tokIO').textContent='(入 '+fmtTok(t.inputTokens)+' / 出 '+fmtTok(t.outputTokens)+cacheStr+' · '+t.calls+'次'+(t.reused?' · 复用'+t.reused:'')+')';
  var rows=[];
  (u.byTask||[]).forEach(function(task){
    (task.byModel||[]).forEach(function(model){
      var r=model.totals||{};
      if(r.calls>0||r.reused>0||r.inputTokens>0||r.outputTokens>0)rows.push({task:task.task,model:model.model,provider:model.provider,totals:r});
    });
  });
  if(!rows.length){$('#usageWrap').style.display='none';return}
  rows.sort(function(a,b){return (b.totals.totalTokens||0)-(a.totals.totalTokens||0)});
  $('#usageWrap').style.display='block';
  var h=['<tr><th>任务</th><th>模型</th><th>调用</th><th>失败</th><th>复用</th><th>入token</th><th title="缓存命中/新建，均为入token的子集">缓存(命中/建)</th><th>出token</th><th>合计</th></tr>'];
  rows.forEach(function(row){
    var r=row.totals,ch=r.cachedInputTokens||0,cn=r.cacheCreationTokens||0;
    h.push('<tr><td>'+(TASKNAMES[row.task]||row.task)+'</td><td class="mono">'+row.provider+'/'+row.model+'</td><td>'+r.calls+'</td><td>'+(r.failed||0)+'</td><td>'+(r.reused||0)+'</td><td>'+fmtTok(r.inputTokens)+'</td><td class="dim">'+(ch||cn?fmtTok(ch)+'/'+fmtTok(cn):'—')+'</td><td>'+fmtTok(r.outputTokens)+'</td><td>'+fmtTok(r.totalTokens)+'</td></tr>');
  });
  h.push('<tr><td class="dim">合计</td><td></td><td class="dim">'+t.calls+'</td><td class="dim">'+t.failed+'</td><td class="dim">'+t.reused+'</td><td class="dim">'+fmtTok(t.inputTokens)+'</td><td class="dim">'+(t.cachedInputTokens||t.cacheCreationTokens?fmtTok(t.cachedInputTokens||0)+'/'+fmtTok(t.cacheCreationTokens||0):'—')+'</td><td class="dim">'+fmtTok(t.outputTokens)+'</td><td class="dim">'+fmtTok(t.totalTokens)+'</td></tr>');
  $('#usageTable').innerHTML=h.join('');
}
function renderEpTiming(tm){
  if(!tm||!tm.episodes||!tm.episodes.length){$('#timingWrap').style.display='none';return}
  $('#timingWrap').style.display='block';
  var rows=['<tr><th>集</th><th>转码</th><th>大小</th><th>上传</th><th>解析</th></tr>'];
  tm.episodes.forEach(function(e){
    var slow=e.generateMs>60000?' class="slow"':'';
    rows.push('<tr><td>ep'+('00'+e.episode).slice(-3)+'</td><td>'+fmtDur(e.transcodeMs)+(e.transcoded===false?'*':'')+'</td><td>'+(e.uploadMB||'?')+'M</td><td>'+fmtDur(e.uploadMs)+'</td><td'+slow+'>'+fmtDur(e.generateMs)+'</td></tr>');
  });
  rows.push('<tr><td class="dim">累计</td><td></td><td></td><td></td><td class="dim">'+fmtDur(tm.totalMs)+'</td></tr>');
  $('#tmTable').innerHTML=rows.join('');
}
// tick the total elapsed locally between polls; serial ETA/rate from the summary
function renderTiming(){
  if(!timing){$('#elapsed').textContent='—';$('#eta').textContent='';return}
  var live=timing.elapsedMs+(timing.done?0:(Date.now()-timingAt));
  $('#elapsed').textContent=fmtDur(live)+(timing.done?'':' …');
  var ser=timing.serial,eta='';
  if(ser){
    eta='串行 '+ser.doneWindows+'/'+ser.totalWindows+' 窗口';
    if(ser.avgWindowMs!=null)eta+=' · ~'+fmtDur(ser.avgWindowMs)+'/窗';
    if(ser.etaMs!=null&&!timing.done)eta+=' · 预计剩余 '+fmtDur(ser.etaMs);
  }
  $('#eta').textContent=eta;
}
$('#follow').onchange=function(e){follow=e.target.checked};
function esc(s){return s.replace(/[&<>]/g,function(c){return{'&':'&amp;','<':'&lt;','>':'&gt;'}[c]})}
function renderContent(rel,text){
  if(rel.endsWith('.jsonl')){$('#view').textContent=text.split('\\n').map(function(l){try{return JSON.stringify(JSON.parse(l))}catch(e){return l}}).join('\\n');return}
  if(rel.endsWith('.json')){try{text=JSON.stringify(JSON.parse(text),null,2)}catch(e){}$('#view').textContent=text;return}
  if(rel.endsWith('.md')){
    $('#view').innerHTML=text.split('\\n').map(function(l){
      if(l.indexOf('## ')===0)return '<h2>'+esc(l.slice(3))+'</h2>';
      if(l.indexOf('# ')===0)return '<h1>'+esc(l.slice(2))+'</h1>';
      if(l.indexOf('  ')===0||l.indexOf('- ')===0||l.indexOf('> ')===0)return '<span class="b">'+esc(l)+'</span>';
      return esc(l)||'&nbsp;';
    }).join('\\n');return}
  $('#view').textContent=text;
}
function loadFile(rel){
  cur=rel;$('#cur').textContent=rel;
  var fs=document.querySelectorAll('.f');for(var i=0;i<fs.length;i++)fs[i].classList.toggle('sel',fs[i].dataset.rel===rel);
  fetch('/api/file?path='+encodeURIComponent(rel)).then(function(r){return r.text()}).then(function(t){renderContent(rel,t)}).catch(function(){});
}
function frac(p){return p.unit&&p.unit.total?p.unit.done/p.unit.total:(p.done?1:0)}
function poll(){
  fetch('/api/state').then(function(r){return r.json()}).then(function(d){
    var s=d.status,passes=s.passes||[];
    $('#root').textContent=d.root;
    $('#stateBadge').className='state '+s.state;
    $('#stateBadge').textContent={ready:'已完成',incomplete:'进行中',failed:'失败',empty:'空'}[s.state]||s.state;
    // overall progress = mean of per-stage fractions
    var sum=0;passes.forEach(function(p){sum+=frac(p)});
    var pct=passes.length?Math.round(100*sum/passes.length):0;
    $('#ovBar').style.width=pct+'%';$('#ovPct').textContent=pct+'%';
    // current stage
    var curP=null;for(var i=0;i<passes.length;i++){if(!passes[i].done){curP=passes[i];break}}
    $('#curState').textContent=s.state==='ready'?'已完成 ✓':(curP?(NAMES[curP.name]||curP.name)+(curP.unit?' · '+curP.unit.done+'/'+curP.unit.total:''):'—');
    // per-phase durations from timings (real wall-clock)
    var ph={};((s.timings&&s.timings.phases)||[]).forEach(function(x){ph[x.name]=x});
    // staged stepper with per-stage bars + real durations
    $('#stages').innerHTML=passes.map(function(p){
      var st=p.done?'done':(p.unit&&p.unit.failed?'fail':(p.unit&&p.unit.done>0||p===curP?'run':'wait'));
      var cnt=p.unit?(p.unit.done+'/'+p.unit.total+(p.unit.failed?' ✗'+p.unit.failed:'')):(p.done?'✓':'—');
      var d=ph[p.name]?('<span class="cnt">'+fmtDur(ph[p.name].ms)+(ph[p.name].running?' …':'')+'</span>'):'';
      return '<div class="stage '+st+'"><div class="stage-h"><span class="dot"></span><b>'+(NAMES[p.name]||p.name)+'</b><span class="cnt">'+cnt+'</span></div><div class="track"><i style="width:'+Math.round(frac(p)*100)+'%"></i></div>'+(d?'<div class="dim" style="margin-top:4px">'+d+'</div>':'')+'</div>';
    }).join('');
    timing=s.timings||null;timingAt=Date.now();renderTiming();
    renderEpTiming(d.timing);
    renderUsage(d.usage);
    // sidebar
    var html='',latest=null;
    Object.keys(d.groups).forEach(function(grp){
      var files=d.groups[grp];if(!files.length)return;html+='<div class="grp">'+grp+'</div>';
      files.forEach(function(f){
        html+='<div class="f" data-rel="'+f.rel+'"><span>'+f.rel.split('/').pop()+'</span><span class="sz">'+(f.size>1024?(f.size/1024).toFixed(0)+'k':f.size)+'</span></div>';
        if(grp==='每窗更新'||grp==='上下文'||grp==='转录'||grp==='解析')latest=f.rel;
      });
    });
    $('#side').innerHTML=html;
    var els=document.querySelectorAll('.f');
    for(var j=0;j<els.length;j++){(function(el){el.onclick=function(){follow=false;$('#follow').checked=false;loadFile(el.dataset.rel)};if(el.dataset.rel===cur)el.classList.add('sel')})(els[j])}
    $('#tick').textContent='● '+new Date().toLocaleTimeString();
    if(follow&&latest&&latest!==lastLatest){lastLatest=latest;loadFile(latest)}
    else if(cur){loadFile(cur)}
  }).catch(function(){$('#tick').textContent='● 离线'});
}
poll();setInterval(poll,1500);setInterval(renderTiming,1000);
</script></body></html>`;
